import type VectorSource from 'ol/source/Vector';
import { type LoadingStrategy } from 'ol/source/Vector';
import { createArcGisVectorSource } from '../arcgis/createArcGisVectorSource.ts';
import { type IMapLayerSource, MapLayerType } from '../MapLayer.ts';
import { createWfsVectorSource } from '../wfs/createWfsVectorSource.ts';

export interface ILayerVectorSourceOptions {
  /**
   * Override della strategia di caricamento. Se assente, ogni protocollo
   * usa il proprio default (tile per ArcGIS, bbox per WFS).
   */
  strategy?: LoadingStrategy;
}

interface IVectorSourceResolver {
  /** True quando la config contiene ciò che serve al protocollo */
  isConfigured(layer: IMapLayerSource): boolean;
  create(
    url: string,
    layer: IMapLayerSource,
    options: ILayerVectorSourceOptions,
  ): VectorSource;
}

/**
 * Resolver di creazione VectorSource per tipo di layer. Per supportare
 * un nuovo protocollo vettoriale è sufficiente aggiungere una entry.
 */
const vectorSourceResolvers: Partial<
  Record<MapLayerType, IVectorSourceResolver>
> = {
  [MapLayerType.ArcGISRestVector]: {
    isConfigured: () => true,
    create: (url, _layer, options) => createArcGisVectorSource(url, options),
  },
  [MapLayerType.WFS]: {
    isConfigured: layer => !!layer.wfs?.typeName,
    create: (url, layer, options) =>
      // biome-ignore lint/style/noNonNullAssertion: garantito da isConfigured
      createWfsVectorSource(url, layer.wfs!, options),
  },
};

/**
 * Crea la VectorSource per un layer vettoriale, delegando al resolver
 * del protocollo. Restituisce null se il tipo non è vettoriale o la
 * configurazione è incompleta (es. WFS senza typeName durante l'editing):
 * il chiamante decide il fallback.
 */
export function createLayerVectorSource(
  layer: IMapLayerSource | undefined,
  options: ILayerVectorSourceOptions = {},
): VectorSource | null {
  if (!layer?.url || layer.type === undefined) return null;

  const resolver = vectorSourceResolvers[layer.type];
  if (!resolver?.isConfigured(layer)) return null;

  return resolver.create(layer.url, layer, options);
}
